import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronRight, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';

const Template1 = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { title: 'Interior Design', description: 'Transform your space with elegant and functional designs', icon: '🏠' },
    { title: 'Architecture', description: 'Modern structures that blend form and function beautifully', icon: '🏛️' },
    { title: 'Renovation', description: 'Breathe new life into your existing spaces with expert care', icon: '🔨' },
    { title: 'Consultation', description: 'Expert guidance to bring your vision to reality', icon: '💡' }
  ];

  const gallery = [
    'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1600573472591-ee6b68d14c68?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=500&h=500&fit=crop'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name && formData.email && formData.message) {
      alert(`Thank you ${formData.name}! We'll get back to you soon.`);
      setFormData({ name: '', email: '', message: '' });
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  // Styles
  const styles = {
    container: {
      minHeight: '100vh',
      background: '#020617',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    header: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      transition: 'all 0.3s ease',
      background: scrolled ? 'rgba(15, 23, 42, 0.95)' : 'transparent',
      backdropFilter: scrolled ? 'blur(20px)' : 'none',
      boxShadow: scrolled ? '0 4px 30px rgba(0, 0, 0, 0.3)' : 'none',
    },
    nav: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '1.5rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logo: {
      fontSize: '1.5rem',
      fontWeight: '700',
      background: 'linear-gradient(135deg, #fbbf24, #ea580c)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    navLinks: {
      display: 'flex',
      gap: '2rem',
      listStyle: 'none',
    },
    navButton: {
      background: 'none',
      border: 'none',
      color: '#ffffff',
      textTransform: 'capitalize',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      fontSize: '1rem',
      fontFamily: 'inherit',
    },
    menuToggle: {
      display: 'none',
      background: 'none',
      border: 'none',
      color: '#ffffff',
      cursor: 'pointer',
    },
    mobileMenu: {
      background: 'rgba(15, 23, 42, 0.98)',
      backdropFilter: 'blur(20px)',
      padding: '2rem',
    },
    heroSection: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      textAlign: 'center',
      padding: '2rem',
    },
    heroBg: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(234, 88, 12, 0.05))',
    },
    blob1: {
      position: 'absolute',
      top: '10%',
      left: '10%',
      width: '300px',
      height: '300px',
      background: 'radial-gradient(circle, rgba(245, 158, 11, 0.3), transparent)',
      borderRadius: '50%',
      filter: 'blur(60px)',
      animation: 'pulse 4s ease-in-out infinite',
    },
    blob2: {
      position: 'absolute',
      bottom: '10%',
      right: '10%',
      width: '400px',
      height: '400px',
      background: 'radial-gradient(circle, rgba(234, 88, 12, 0.3), transparent)',
      borderRadius: '50%',
      filter: 'blur(80px)',
      animation: 'pulse 4s ease-in-out infinite 2s',
    },
    heroContent: {
      position: 'relative',
      zIndex: 10,
      maxWidth: '1000px',
    },
    h1: {
      fontSize: 'clamp(3rem, 8vw, 6rem)',
      fontWeight: '800',
      marginBottom: '1.5rem',
      background: 'linear-gradient(135deg, #fbbf24, #ea580c)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    heroSubtitle: {
      fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
      color: '#cbd5e1',
      marginBottom: '2rem',
    },
    ctaButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      background: 'linear-gradient(135deg, #f59e0b, #ea580c)',
      color: 'white',
      padding: '1rem 2rem',
      borderRadius: '50px',
      border: 'none',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    section: {
      padding: '6rem 2rem',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    h2: {
      fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
      fontWeight: '700',
      textAlign: 'center',
      marginBottom: '3rem',
      background: 'linear-gradient(135deg, #fbbf24, #ea580c)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    aboutGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '3rem',
      alignItems: 'center',
    },
    aboutText: {
      color: '#cbd5e1',
      fontSize: '1.1rem',
      marginBottom: '1.5rem',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '2rem',
      marginTop: '2rem',
    },
    statItem: {
      textAlign: 'center',
    },
    statNumber: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#f59e0b',
    },
    statLabel: {
      color: '#94a3b8',
      fontSize: '0.9rem',
    },
    aboutImage: {
      position: 'relative',
    },
    aboutImg: {
      width: '100%',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
    },
    imageDecoration: {
      position: 'absolute',
      bottom: '-20px',
      right: '-20px',
      width: '200px',
      height: '200px',
      background: 'linear-gradient(135deg, #f59e0b, #ea580c)',
      borderRadius: '20px',
      zIndex: -1,
    },
    servicesSection: {
      background: '#1e293b',
    },
    servicesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '2rem',
    },
    serviceCard: {
      background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.8))',
      padding: '2.5rem',
      borderRadius: '20px',
      border: '1px solid rgba(245, 158, 11, 0.1)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    serviceIcon: {
      fontSize: '3rem',
      marginBottom: '1rem',
    },
    serviceTitle: {
      color: '#f59e0b',
      fontSize: '1.5rem',
      marginBottom: '1rem',
      fontWeight: '700',
    },
    serviceDesc: {
      color: '#cbd5e1',
    },
    galleryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1rem',
    },
    galleryItem: {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '15px',
      aspectRatio: '1',
      cursor: 'pointer',
      transition: 'transform 0.3s ease',
    },
    galleryImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.5s ease',
    },
    contactGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '3rem',
    },
    contactTitle: {
      fontSize: '1.8rem',
      marginBottom: '1rem',
      fontWeight: '700',
    },
    contactText: {
      color: '#cbd5e1',
      marginBottom: '2rem',
    },
    contactItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    formInput: {
      width: '100%',
      background: '#1e293b',
      border: '1px solid rgba(245, 158, 11, 0.2)',
      borderRadius: '10px',
      padding: '1rem',
      color: '#ffffff',
      fontSize: '1rem',
      marginBottom: '1.5rem',
      fontFamily: 'inherit',
    },
    formTextarea: {
      width: '100%',
      background: '#1e293b',
      border: '1px solid rgba(245, 158, 11, 0.2)',
      borderRadius: '10px',
      padding: '1rem',
      color: '#ffffff',
      fontSize: '1rem',
      marginBottom: '1.5rem',
      resize: 'vertical',
      minHeight: '120px',
      fontFamily: 'inherit',
    },
    submitButton: {
      width: '100%',
      background: 'linear-gradient(135deg, #f59e0b, #ea580c)',
      color: 'white',
      padding: '1rem',
      border: 'none',
      borderRadius: '10px',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    footer: {
      background: '#1e293b',
      padding: '3rem 2rem',
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '3rem',
      marginBottom: '2rem',
    },
    footerTitle: {
      color: '#f59e0b',
      marginBottom: '1rem',
      fontWeight: '700',
    },
    footerText: {
      color: '#94a3b8',
      marginBottom: '0.5rem',
    },
    footerLink: {
      color: '#94a3b8',
      textDecoration: 'none',
      display: 'block',
      marginBottom: '0.5rem',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      background: 'none',
      border: 'none',
      textAlign: 'left',
      fontSize: '1rem',
      fontFamily: 'inherit',
      padding: 0,
    },
    socialLinks: {
      display: 'flex',
      gap: '1rem',
    },
    socialIcon: {
      background: 'none',
      border: 'none',
      color: '#ffffff',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
    },
    footerBottom: {
      maxWidth: '1200px',
      margin: '0 auto',
      paddingTop: '2rem',
      borderTop: '1px solid rgba(245, 158, 11, 0.2)',
      textAlign: 'center',
      color: '#94a3b8',
    },
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 0.6; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.1); }
          }
          .nav-button:hover { color: #f59e0b; }
          .cta-button:hover { transform: scale(1.05); box-shadow: 0 10px 40px rgba(245, 158, 11, 0.4); }
          .service-card:hover { transform: translateY(-10px) scale(1.02); box-shadow: 0 20px 60px rgba(245, 158, 11, 0.2); border-color: #f59e0b; }
          .gallery-item:hover img { transform: scale(1.1); }
          .submit-button:hover { transform: scale(1.02); box-shadow: 0 10px 30px rgba(245, 158, 11, 0.4); }
          .footer-link:hover { color: #f59e0b; }
          .social-icon:hover { color: #f59e0b; }
          @media (max-width: 768px) {
            .menu-toggle { display: block !important; }
            .nav-links { display: none !important; }
            .stats-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 1rem !important; }
          }
        `}
      </style>

      {/* Header */}
      <header style={styles.header}>
        <nav style={styles.nav}>
          <div style={styles.logo}>LUXURA BUILD</div>
          <div style={styles.navLinks} className="nav-links">
            {['home', 'about', 'services', 'gallery', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                style={styles.navButton}
                className="nav-button"
              >
                {item}
              </button>
            ))}
          </div>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} style={styles.menuToggle} className="menu-toggle">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {isMenuOpen && (
          <div style={styles.mobileMenu}>
            {['home', 'about', 'services', 'gallery', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                style={{...styles.navButton, display: 'block', width: '100%', textAlign: 'left', marginBottom: '1rem'}}
                className="nav-button"
              >
                {item}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" style={styles.heroSection}>
        <div style={styles.heroBg}>
          <div style={styles.blob1}></div>
          <div style={styles.blob2}></div>
        </div>
        <div style={styles.heroContent}>
          <h1 style={styles.h1}>Build Your Dream Space</h1>
          <p style={styles.heroSubtitle}>
            Premium construction and design solutions that turn your vision into reality
          </p>
          <button onClick={() => scrollToSection('contact')} style={styles.ctaButton} className="cta-button">
            Start Your Project
            <ChevronRight />
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" style={styles.section}>
        <div style={styles.aboutGrid}>
          <div>
            <h2 style={styles.h2}>About Luxura Build</h2>
            <p style={styles.aboutText}>
              For over 15 years, Luxura Build has been at the forefront of innovative construction and design. We specialize in creating spaces that inspire, comfort, and elevate everyday living.
            </p>
            <p style={styles.aboutText}>
              Our team of expert architects, designers, and craftsmen work together to deliver exceptional results that exceed expectations. From concept to completion, we ensure every detail reflects your unique style and needs.
            </p>
            <div style={styles.statsGrid} className="stats-grid">
              <div style={styles.statItem}>
                <div style={styles.statNumber}>850+</div>
                <div style={styles.statLabel}>Projects</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statNumber}>600+</div>
                <div style={styles.statLabel}>Happy Clients</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statNumber}>25+</div>
                <div style={styles.statLabel}>Awards</div>
              </div>
            </div>
          </div>
          <div style={styles.aboutImage}>
            <img
              src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=600&h=600&fit=crop"
              alt="Modern architecture"
              style={styles.aboutImg}
            />
            <div style={styles.imageDecoration}></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" style={{...styles.section, ...styles.servicesSection}}>
        <h2 style={styles.h2}>Our Services</h2>
        <div style={styles.servicesGrid}>
          {services.map((service, index) => (
            <div key={index} style={styles.serviceCard} className="service-card">
              <div style={styles.serviceIcon}>{service.icon}</div>
              <h3 style={styles.serviceTitle}>{service.title}</h3>
              <p style={styles.serviceDesc}>{service.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" style={styles.section}>
        <h2 style={styles.h2}>Featured Projects</h2>
        <div style={styles.galleryGrid}>
          {gallery.map((img, index) => (
            <div key={index} style={styles.galleryItem} className="gallery-item">
              <img src={img} alt={`Project ${index + 1}`} style={styles.galleryImg} />
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={styles.section}>
        <h2 style={styles.h2}>Let's Build Together</h2>
        <div style={styles.contactGrid}>
          <div>
            <h3 style={styles.contactTitle}>Start Your Project</h3>
            <p style={styles.contactText}>
              Ready to transform your space? Get in touch with our team today. We'll discuss your vision and create a customized plan that brings your dream to life.
            </p>
            <div style={styles.contactItem}>
              <Mail style={{color: '#f59e0b'}} />
              <span>contact@luxurabuild.com</span>
            </div>
            <div style={styles.contactItem}>
              <Phone style={{color: '#f59e0b'}} />
              <span>+1 (555) 987-6543</span>
            </div>
            <div style={styles.contactItem}>
              <MapPin style={{color: '#f59e0b'}} />
              <span>456 Design Avenue, Build City, BC 54321</span>
            </div>
          </div>
          <div>
            <input
              type="text"
              placeholder="Your Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={styles.formInput}
            />
            <input
              type="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              style={styles.formInput}
            />
            <textarea
              placeholder="Tell us about your project"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              style={styles.formTextarea}
            />
            <button onClick={handleSubmit} style={styles.submitButton} className="submit-button">
              Send Enquiry
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div>
            <div style={styles.logo}>LUXURA BUILD</div>
            <p style={styles.footerText}>
              Crafting exceptional spaces with precision and passion.
            </p>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Quick Links</h4>
            {['Home', 'About', 'Services', 'Gallery', 'Contact'].map((link) => (
              <button
                key={link}
                onClick={() => scrollToSection(link.toLowerCase())}
                style={styles.footerLink}
                className="footer-link"
              >
                {link}
              </button>
            ))}
          </div>
          <div>
            <h4 style={styles.footerTitle}>Follow Us</h4>
            <div style={styles.socialLinks}>
              <button style={styles.socialIcon} className="social-icon">
                <Facebook size={24} />
              </button>
              <button style={styles.socialIcon} className="social-icon">
                <Twitter size={24} />
              </button>
              <button style={styles.socialIcon} className="social-icon">
                <Instagram size={24} />
              </button>
              <button style={styles.socialIcon} className="social-icon">
                <Linkedin size={24} />
              </button>
            </div>
          </div>
        </div>
        <div style={styles.footerBottom}>
          <p>&copy; 2025 Luxura Build. All rights reserved.</p>
          <Link to="/build-your-website" style={{...styles.submitButton, marginTop: '1rem', display: 'inline-block', textDecoration: 'none'}}>Go Back</Link>
        </div>
      </footer>
    </div>
  );
};

export default Template1;